// Runtime configuration for the commercial layer.
//
// IMPORTANT: only PUBLIC values live here. The Supabase URL and the *anon* key
// are designed to be shipped in the client (RLS + the verify_jwt Edge Functions
// are what protect data). The AI provider keys (fal/openrouter) and the service
// role key NEVER reach the client — they live only in Edge Function secrets.
//
// Values are injected at build time by Vite (`import.meta.env.*`) and can be
// overridden at runtime via `window.__JOEFLOW_CONFIG__` so the same bundle can
// be pointed at staging vs production without a rebuild.

export interface CommerceConfig {
  /** e.g. https://abcd.supabase.co */
  supabaseUrl: string;
  /** Public anon key (safe for the client). */
  supabaseAnonKey: string;
  /** RevenueCat public SDK key (iOS/Android pick the right one at runtime). */
  revenueCatApiKey: string;
  /**
   * Base URL of a LOCAL image generator (SD-WebUI / ComfyUI) on the user's own
   * machine, e.g. http://localhost:7860. Empty disables the free 'local' tier.
   */
  localAiUrl: string;
  /**
   * Stripe TEST-mode publishable key (pk_test_…) for the print-shop checkout
   * mock-up. Public + test-only, so safe in the client. Empty → the print shop
   * uses its built-in visual mock checkout instead of the real Payment Element.
   */
  stripePkTest: string;
}

interface RuntimeOverride {
  supabaseUrl?: string;
  supabaseAnonKey?: string;
  revenueCatApiKey?: string;
  localAiUrl?: string;
  stripePkTest?: string;
}

declare global {
  interface Window {
    __JOEFLOW_CONFIG__?: RuntimeOverride;
  }
}

// Vite replaces these at build time. Missing values fall back to '' so the app
// can still boot in a degraded (offline/anonymous) state instead of crashing.
const BUILD_DEFAULTS: CommerceConfig = {
  supabaseUrl: readEnv('VITE_SUPABASE_URL'),
  supabaseAnonKey: readEnv('VITE_SUPABASE_ANON_KEY'),
  revenueCatApiKey: readEnv('VITE_REVENUECAT_API_KEY'),
  localAiUrl: readEnv('VITE_LOCAL_AI_URL'),
  stripePkTest: readEnv('VITE_STRIPE_PK_TEST'),
};

function readEnv(key: string): string {
  // `import.meta.env` only exists under Vite; guard so unit tests (node) work.
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  return (env && env[key]) || '';
}

let cached: CommerceConfig | null = null;

/** Resolved config: runtime override (window) wins over build-time defaults. */
export function getConfig(): CommerceConfig {
  if (cached) return cached;
  const override = (typeof window !== 'undefined' && window.__JOEFLOW_CONFIG__) || {};
  cached = {
    supabaseUrl: override.supabaseUrl || BUILD_DEFAULTS.supabaseUrl,
    supabaseAnonKey: override.supabaseAnonKey || BUILD_DEFAULTS.supabaseAnonKey,
    revenueCatApiKey: override.revenueCatApiKey || BUILD_DEFAULTS.revenueCatApiKey,
    localAiUrl: override.localAiUrl || BUILD_DEFAULTS.localAiUrl,
    stripePkTest: override.stripePkTest || BUILD_DEFAULTS.stripePkTest,
  };
  return cached;
}

/** True when the Supabase backend is configured (else run anonymous/offline). */
export function isBackendConfigured(): boolean {
  const c = getConfig();
  return c.supabaseUrl.length > 0 && c.supabaseAnonKey.length > 0;
}

/** Test-only: reset the memoised config. */
export function __resetConfigCache(): void {
  cached = null;
}
